import {
  styleTranslationMap,
  sanityToTailwindBreakpoint,
} from "@bared/ui/theme";

/**
 * @typedef {import("@bared/ui/theme").ThemeToken} ThemeToken
 * @typedef {import("@bared/ui/theme").ThemeTokenMap} ThemeTokenMap
 */

/**
 * @typedef {import("../types/sanity").Styles} Styles
 * @typedef {Extract<keyof Styles, {
 *   key: string
 *   primitive: string
 * }>} StyleKey
 */

/**
 * Returns the primitive value from the translation map for the given key.
 * @param {keyof styleTranslationMap} key
 * @returns {any} The translation map item e.g. { mapKey: mull, primitive: "items", values: [{ title: "Start", value: "start" }] }
 */
const mapKeyToPrimitive = (key) => {
  if (!key) {
    return null;
  }

  return styleTranslationMap[key]?.primitive;
};

/**
 * Generates a class name with breakpoint prefix if needed. If the breakpoint is "default", no prefix is added.
 * @param {string} className - e.g. "p-none" or "flex"
 * @param {string} [breakpoint] - e.g. "default" or "sm"
 * @param {string} [prefix] - e.g. "hover", "last-of-type", "last", "first", "first-of-type", "focus"
 * @returns {string} A Tailwind className e.g. "p-none" or "sm:p-none"
 */
export const generateClassName = (className, breakpoint, prefix) => {
  if (breakpoint === "default" || !breakpoint) {
    if (prefix) {
      return `${prefix}:${className}`;
    }
    return className;
  }

  if (prefix) {
    return `${prefix}:${breakpoint}:${className}`;
  }

  return `${breakpoint}:${className}`;
};

/**
 * Returns the className for a given breakpoint/primitive from Sanity types
 * @param {StyleKey} key - e.g. "default_padding", "sm_padding", "md_sticky"
 * @param {Styles} set
 * @returns {any} The class name, e.g. "p-none", "sm:p-none", or "md:sticky"
 */
export const mapKeyToClassName = (key, set) => {
  const [_breakpoint, prop] = key.split("_");

  const breakpoint = sanityToTailwindBreakpoint(_breakpoint);

  if (!prop) {
    return null;
  }

  const tokenMapKey = styleTranslationMap[prop]?.mapKey;
  const primitive = mapKeyToPrimitive(prop);
  const state = set[`${breakpoint}_state`];
  const descendants = set[`${breakpoint}_descendants`];

  let prefix = "";

  if (descendants && state) {
    prefix = `[&_${descendants}:${state}]`;
  } else if (state && !descendants) {
    prefix = state;
  } else if (descendants && !state) {
    prefix = `[&_${descendants}]`;
  }

  /** @type string | Record<string, string> */
  const value = set[key];

  // Reserved for primitives like "flex", "absolute", etc.
  if (tokenMapKey === null && !primitive && !state) {
    return generateClassName(value, breakpoint);
  }

  if (!primitive) {
    return null;
  }

  let className = `${primitive}-${value}`;

  if (typeof value === "object" && "value" in value) {
    // Custom colour
    if (value["key"] === "custom") {
      className = `${primitive}-[${value["value"]}]`;
    } else {
      className = `${primitive}-${value["key"]}`;
    }
  }

  return generateClassName(className, breakpoint, prefix);
};

/**
 * Returns a list of class names based on a set of styles from Sanity
 * @param {Styles[] | null} [styles] - e.g. [{"default_padding": "pico"}]
 * @returns {string} A list of class names, e.g. ["p-pico"]
 */
export const mapStylesToClassNames = (styles) => {
  if (!styles || !Array.isArray(styles)) {
    return "";
  }

  const result = styles
    .filter(Boolean)
    .map((set) => {
      const keys = Object.keys(set);
      const styleSet = keys
        .map((key) => mapKeyToClassName(key, set))
        .filter((className) => !!className);
      return styleSet;
    })
    .flat();

  return result.join(" ");
};
